// src/components/auth/ProtectedRoute.jsx
// STATUS: stable | UPDATED: 2026-08-02
// LOG:
//   2026-08-02  DEV-only bypass via VITE_DEV_BYPASS_AUTH for local UI work
//               without Supabase keys. import.meta.env.DEV guard means the
//               branch is dead code in production builds and stripped.
//   2026-08-30  minRole guards. The NEXT note below is now done.
//   2026-08-02  Header standardized; Phase 1 checkpoint committed (e765830).
// Route guard. Redirects to /login/ if the user is not authenticated.
// Renders children (or nested route via Outlet) if authenticated.

import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { atLeast } from '../../lib/nav'

const DEV_BYPASS = import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS_AUTH === '1'

// `minRole` is the least senior role allowed here, so minRole="manager" admits
// manager, owner and admin.
//
// This is a second line, not the line. RLS is what actually stops a salesperson
// reading a floor, and it has been probed from a rival account. This exists so
// that somebody who types a URL they were never offered gets sent home instead
// of landing on a working page full of empty states.
export default function ProtectedRoute({ children, requireAdmin = false, minRole = null }) {
  const { isAuthenticated, isAdmin, role, loading, mustSetPassword } = useAuth()
  const location = useLocation()

  if (DEV_BYPASS) return children || <Outlet />

  if (loading) return null

  // A recovery session is not a login. Until a new password is chosen there is
  // exactly one page this person may see.
  if (mustSetPassword) return <Navigate to="/reset-password/" replace />

  if (!isAuthenticated) {
    return <Navigate to="/login/" state={{ from: location }} replace />
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard/" replace />
  }

  if (minRole && !atLeast(role, minRole)) {
    return <Navigate to="/dashboard/" replace />
  }

  return children || <Outlet />
}
